import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { Link } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { UIProvider } from '@vnetwork/ui/provider';
import { TooltipProvider } from '@vnetwork/ui/primitives';
import { ToastProvider, ErrorBoundary } from '@vnetwork/ui/feedback';

import App from '@/App';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider } from '@/auth/AuthProvider';
import { initSentry, reportErrorBoundary } from '@/lib/sentry';
import { resolveInitialTheme, persistTheme } from '@/lib/theme-preference';
import { captureAttribution } from '@/lib/attribution';
import { initAnalytics } from '@/lib/analytics';
import { initGtm } from '@/lib/gtm';
import { applyAttributionUserProps } from '@/lib/analytics-events';
import { startPageViewTracking } from '@/lib/analytics-page-view';
import { initPerformance } from '@/lib/performance';

// Self-hosted base fonts (bundled into the build, served same-origin) so the UI
// never blocks on / falls back from the Google Fonts CDN on flaky links.
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import './styles/global.css';

initSentry();

// Google Tag Manager — loads the active brand's container (no-op when the brand
// defines no id). Kept separate from Firebase Analytics; both can run together.
initGtm();

// Capture UTM + referral params on first load, before the router can redirect
// and strip the query (e.g. the `utm_campaign=waf_free` spotlight link).
captureAttribution();

// Firebase Analytics (→ GA4). Init, mirror captured attribution into user
// properties, and start SPA page-view tracking. All no-op without a measurement
// id, so this is safe in local dev / preview builds.
void initAnalytics().then(() => {
  applyAttributionUserProps();
});
startPageViewTracking();

// Firebase Performance Monitoring — auto page-load + network-request traces.
initPerformance();

const root = ReactDOM.createRoot(document.getElementById('root')!);

root.render(
  <StrictMode>
    <UIProvider defaultTheme={resolveInitialTheme()} onThemeChange={persistTheme} linkComponent={Link}>
      <ErrorBoundary onError={reportErrorBoundary}>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <TooltipProvider>
              <AuthProvider>
                <App />
              </AuthProvider>
            </TooltipProvider>
          </ToastProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </UIProvider>
  </StrictMode>,
);
